import moment from 'moment'
import getPrioritizedBedbankAmenities from 'lib/offer/getPrioritizedBedbankAmenities'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import type { OfferDetailResult, TripItineraryData } from 'tripPlanner/api/types'
import { findOfferItemInItinerary } from 'tripPlanner/utils/chatItinerary'
import { isHotelSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import type { DrawerHotel } from './types'
import { mapImages } from './mapToDrawerHotel'

/** Offer summary shape that may carry LUX+ pricing — narrow subset of App.HotelOffer/BedbankOffer. */
type MemberPriceSummaryOffer = App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary

export function mapOfferDetailInclusions(detail: OfferDetailResult): NonNullable<DrawerHotel['inclusions']> {
  return detail.inclusions?.map((inc, index) => ({
    id: `${detail.offerId}-inc-${index}`,
    description: inc.text,
    symbol: (inc.icon ?? undefined) as App.OfferFeatureSymbol | undefined,
    isHighlighted: inc.isHighlighted,
  })) ?? []
}

export function mapOfferDetailAmenities(detail: OfferDetailResult): DrawerHotel['amenities'] {
  return detail.amenities?.map((amenity) => ({
    name: amenity.name,
    icon: amenity.icon,
  })) ?? []
}

export function mergeDrawerHotelWithOfferDetail(
  hotel: DrawerHotel,
  detail: OfferDetailResult,
): DrawerHotel {
  return {
    ...hotel,
    rating: detail.rating ?? hotel.rating,
    reviewsTotal: detail.reviewsTotal ?? hotel.reviewsTotal,
    inclusions: mapOfferDetailInclusions(detail),
    amenities: mapOfferDetailAmenities(detail),
  }
}

export function mapOfferToDrawerHotelTileFeatures(
  offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary,
): Pick<DrawerHotel, 'inclusions' | 'tileAmenities'> {
  if (isBedbankOffer(offer)) {
    return {
      inclusions: [],
      tileAmenities: getPrioritizedBedbankAmenities({
        popularFacilities: offer.popularFacilities,
      }),
    }
  }

  if (offer.tileInclusions?.length) {
    return {
      inclusions: offer.tileInclusions,
      tileAmenities: [],
    }
  }

  const lowestPackageRateInclusions = offer.lowestPricePackage?.inclusions?.filter(
    (inclusion) => inclusion.displayContext === 'Rate',
  ) ?? []

  if (lowestPackageRateInclusions.length) {
    return {
      inclusions: lowestPackageRateInclusions,
      tileAmenities: [],
    }
  }

  if (offer.lowestPricePackage?.roomType.amenities.length) {
    return {
      inclusions: [],
      tileAmenities: offer.lowestPricePackage.roomType.amenities,
    }
  }

  const packageRateInclusions = offer.packages.find((pkg) => pkg.inclusions?.length)?.inclusions?.filter(
    (inclusion) => inclusion.displayContext === 'Rate',
  ) ?? []

  return {
    inclusions: [],
    tileAmenities: packageRateInclusions,
  }
}

/**
 * When an offer detail lookup returns no price (e.g. SPP call with dates that
 * have no availability), fills missing pricing fields from the saved itinerary
 * item for the same offer. Only fills fields that are currently missing — never
 * overwrites a live API value.
 */
export function applySavedHotelPriceFallback(
  hotel: DrawerHotel,
  itineraryData: TripItineraryData | undefined,
): DrawerHotel {
  if (hotel.price || !itineraryData) return hotel

  const item = findOfferItemInItinerary(hotel.offerId, itineraryData)
  const savedData = item?.savedItemData
  if (!savedData || !isHotelSavedData(savedData)) return hotel
  if (savedData.price === undefined || savedData.price <= 0) return hotel

  const nights = savedData.checkIn && savedData.checkOut
    ? Math.max(1, moment(savedData.checkOut).diff(moment(savedData.checkIn), 'days'))
    : 1

  return {
    ...hotel,
    price: {
      total: savedData.price,
      nights,
      currency: savedData.currencyCode,
    },
    luxPlusPrice: hotel.luxPlusPrice ?? savedData.memberPrice,
    value: hotel.value ?? savedData.value,
    discountPercent: hotel.discountPercent ?? savedData.discountPercent,
    memberDiscountPercent: hotel.memberDiscountPercent ?? savedData.memberDiscountPercent,
  }
}

/**
 * Falls back to the public offer summary's lowestPricePackage.memberPrice when the
 * detail result has no LUX+ price (e.g. search-level tile data that preceded the detail
 * fetch). Does nothing for bedbank (no LUX+ pricing) or when the hotel already has a
 * luxPlusPrice. Only fills missing fields — never overwrites a live API value.
 */
export function applyOfferSummaryMemberPriceFallback(
  hotel: DrawerHotel,
  summaryOffer: MemberPriceSummaryOffer | undefined,
): DrawerHotel {
  if (hotel.luxPlusPrice !== undefined || !summaryOffer) return hotel
  if (isBedbankOffer(summaryOffer)) return hotel

  const pkg = summaryOffer.lowestPricePackage
  const memberPrice = pkg?.memberPrice
  if (!memberPrice || memberPrice <= 0) return hotel
  if (hotel.price && memberPrice >= hotel.price.total) return hotel

  return {
    ...hotel,
    luxPlusPrice: memberPrice,
  }
}

export function mapOfferDetailToDrawerHotel(detail: OfferDetailResult): DrawerHotel {
  return {
    id: detail.offerId,
    offerId: detail.offerId,
    offerType: detail.offerType,
    slug: detail.slug,
    name: detail.name,
    description: detail.description,
    location: detail.location,
    images: mapImages(detail.images),
    rating: detail.rating,
    reviewsTotal: detail.reviewsTotal,
    inclusions: mapOfferDetailInclusions(detail),
    amenities: mapOfferDetailAmenities(detail),
    price: detail.price !== undefined ? {
      total: detail.price,
      nights: detail.duration ?? 1,
      currency: detail.currencyCode,
    } : undefined,
    luxPlusPrice: detail.memberPrice,
    value: detail.value,
    discountPercent: detail.discountPercent,
    memberDiscountPercent: detail.memberDiscountPercent,
    geoLocation: detail.geoLocation,
    deeplinkUrl: detail.deeplinkUrl,
    saleUnit: detail.saleUnit,
    roomOptions: detail.roomOptions?.map((room) => ({
      roomId: room.roomId,
      name: room.name,
      imageIds: room.imageIds,
      amenities: room.amenities.map((a) => ({
        name: a.name,
        icon: a.icon,
      })),
      sizeSqm: room.sizeSqm,
      bedGroupDescriptions: room.bedGroupDescriptions,
      capacities: room.capacities,
      urgencyTags: room.urgencyTags,
      packageCount: room.packageCount,
      lowestPrice: room.lowestPrice,
      currencyCode: room.currencyCode,
    })),
    bookedRooms: detail.bookedRooms?.map((room) => ({
      roomTypeName: room.roomTypeName,
      packageName: room.packageName,
      roomImageIds: room.roomImageIds,
      checkIn: room.checkIn,
      checkOut: room.checkOut,
      orderId: room.orderId,
      bookingNumber: room.bookingNumber,
    })),
  }
}
